import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny } from '../../api/client';
import { deleteScene, fetchGroups, fetchScenes, runScene, saveScene } from '../../api/ops';
import { Card, Field, Modal } from '../../ui/components';
import { useMe } from '../auth/session';
import { useReportScope } from '../reports/ReportScope';
import { levelText } from './GroupsPage';

/** Scenes: levels for several working groups at once (e.g. festive lighting, events, emergency full light). */
export function ScenesTab() {
  const { t } = useTranslation();
  const me = useMe().data;
  const { clientId } = useReportScope();
  const scenes = useQuery({
    queryKey: ['scenes', clientId],
    queryFn: () => fetchScenes(clientId),
    enabled: !!clientId,
  });
  const groups = useQuery({
    queryKey: ['groups', clientId],
    queryFn: () => fetchGroups(clientId),
    enabled: !!clientId,
  });
  const [editing, setEditing] = useState<api.Scene | 'new' | null>(null);
  const [ran, setRan] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: runScene,
    onSuccess: (r) => setRan(t('groupsPage.sceneRan', { groups: r.events, devices: r.devices })),
  });
  const gname = (id: string) => groups.data?.items.find((g) => g.id === id)?.name ?? '?';
  return (
    <Card
      title={t('groupsPage.scenes')}
      bodyClass=""
      actions={
        canAny(me, 'scene.manage') && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!clientId}
            onClick={() => setEditing('new')}
          >
            <Plus size={15} /> {t('groupsPage.newScene')}
          </button>
        )
      }
    >
      <div className="card-body muted small" style={{ paddingBottom: 0 }}>
        {t('groupsPage.scenesHint')}
        {ran && <p className="form-ok">{ran}</p>}
        {run.isError && <p className="form-error">{t('errors.FORBIDDEN')}</p>}
      </div>
      <div className="ops-scroll">
        <table className="table table-wrap">
          <tbody>
            {(scenes.data?.items ?? []).map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{s.name}</strong>
                  <span className="cell-sub">
                    {s.actions.map((a) => `${gname(a.group_id)} → ${levelText(a.level, t)}`).join(' · ')}
                  </span>
                </td>
                <td className="muted small">
                  {s.duration_min
                    ? t('groupsPage.forMinutes', { n: s.duration_min })
                    : t('groupsPage.untilNext')}
                </td>
                <td className="right nowrap">
                  {s.can_execute && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={run.isPending}
                      onClick={() => (setRan(null), run.mutate(s.id))}
                    >
                      <Play size={14} /> {t('groupsPage.run')}
                    </button>
                  )}
                  {s.can_manage && (
                    <button type="button" className="btn btn-ghost" onClick={() => setEditing(s)}>
                      {t('common.edit')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {scenes.data?.items.length === 0 && (
              <tr>
                <td className="muted">{t('groupsPage.noScenes')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && clientId && (
        <SceneModal
          clientId={clientId}
          scene={editing === 'new' ? null : editing}
          groups={groups.data?.items ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function SceneModal({
  clientId,
  scene,
  groups,
  onClose,
}: {
  clientId: string;
  scene: api.Scene | null;
  groups: api.Group[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(scene?.name ?? '');
  const [duration, setDuration] = useState(scene?.duration_min ? String(scene.duration_min) : '');
  const [actions, setActions] = useState<{ group_id: string; level: number }[]>(
    scene?.actions ?? [{ group_id: groups[0]?.id ?? '', level: 100 }],
  );
  const save = useMutation({
    mutationFn: () =>
      saveScene(scene?.id ?? null, {
        client_id: clientId,
        name,
        actions: actions.filter((a) => a.group_id),
        duration_min: duration ? Number(duration) : null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scenes'] });
      onClose();
    },
  });
  const del = useMutation({
    mutationFn: () => deleteScene(scene!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scenes'] });
      onClose();
    },
  });
  return (
    <Modal title={scene ? scene.name : t('groupsPage.newScene')} onClose={onClose}>
      <div className="form-grid">
        <Field label={t('common.name')}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </Field>
        <Field label={t('groupsPage.duration')} hint={t('groupsPage.durationHint')}>
          <input
            className="input"
            type="number"
            min={1}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </Field>
      </div>
      <h4>{t('groupsPage.actions')}</h4>
      {actions.map((a, i) => (
        <div key={i} className="inline-form" style={{ marginBottom: 8 }}>
          <select
            className="input"
            value={a.group_id}
            aria-label={t('groupsPage.groups')}
            onChange={(e) =>
              setActions(actions.map((x, j) => (j === i ? { ...x, group_id: e.target.value } : x)))
            }
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <input
            className="input"
            type="number"
            min={0}
            max={100}
            value={a.level}
            aria-label={t('groupsPage.level')}
            onChange={(e) =>
              setActions(
                actions.map((x, j) =>
                  j === i ? { ...x, level: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } : x,
                ),
              )
            }
            style={{ width: 90 }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            aria-label={t('common.remove')}
            onClick={() => setActions(actions.filter((_, j) => j !== i))}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn"
        onClick={() => setActions([...actions, { group_id: groups[0]?.id ?? '', level: 100 }])}
      >
        <Plus size={14} /> {t('groupsPage.addAction')}
      </button>
      {save.isError && <p className="form-error">{t('errors.INVALID_REQUEST')}</p>}
      <div className="form-actions">
        {scene && (
          <button type="button" className="btn btn-danger" onClick={() => del.mutate()}>
            {t('common.delete')}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!name.trim() || !actions.some((a) => a.group_id) || save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
}
